import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { useStripe, useElements } from "@stripe/react-stripe-js";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";

import { toastMessageError } from "../../utilities/commonToastMessage";
import CommonButton from "../../utilities/formElements/commonButton/CommonButton";

import storage from "../../../util/storage";
import { persistTierLocalStorage } from "../../../util/entitlements";

import { subscription } from "../../../services/susbscription";
import { currentUser } from "../../../services/auth";

import { ISubscription } from "../../../Interfaces/SettingInterface";
import { IPaymentData } from "../../../Interfaces/CommonInterface";

import {
  baseJumpPlan,
  fourteenDaysPlan,
  paymentFormPlans,
  PREMIUM_FEATURE_LABELS,
  FREE_TIER_FEATURE_LABELS,
} from "../../../constants/commonConstants";
import { countries } from "../../../constants/commonConstants";

import ActionType from "../../../resources/enums";
import CongratsModel from "../../commonModel/InfoModel";

import LockIcon from "../../../assets/images/lock-green.svg";
import Check from "../../../assets/images/checkIcon.svg";
import { eventEmitter } from "../../../eventEmitters/EventEmitters";

export interface PaymentModalProps {
  className?: string;
  title?: string;
  onClose?: any;
  children?: React.ReactNode;
  closeModel?: any;
  subscriptionData?: ISubscription;
  paymentMethodData?: IPaymentData;
}

export interface IErrors {
  incomplete_number?: string;
  incomplete_expiry?: string;
  incomplete_cvc?: string;
  invalid_number?: string;
  invalid_expiry_year_past?: string;
}

const PaymentModel: React.FC<PaymentModalProps> = ({
  subscriptionData,
  paymentMethodData,
}) => {
  const authReducer = useSelector((state: any) => state?.AuthReducer);
  const dispatch = useDispatch();
  const { t: translation } = useTranslation();

  const stripe = useStripe();
  const elements = useElements();

  const [paymentData, setPaymentData] = useState<IPaymentData | any>(
    paymentMethodData
  );
  const [addNewCard, setAddNewCard] = useState<boolean>(true);

  const [selected, setSelected] = useState<number>();
  const [currentPlan, setCurrentPlan] = useState(
    subscriptionData?.subscriptionData?.title
  );
  // Which interval the account is CURRENTLY billed on, read from the same
  // account record `currentPlan` takes its title from. Needed so the "hide the
  // plan you already have" rule can compare tier AND interval - see the plan
  // grid below for why comparing tier alone silently made annual unreachable
  // for anyone wanting it on their existing plan. Defaults to 'monthly' for
  // accounts predating the annual-billing work, which is what they are on.
  const currentBillingInterval: "monthly" | "annual" =
    subscriptionData?.subscriptionData?.billingInterval === "annual"
      ? "annual"
      : "monthly";
  const [subscriptionTitle, setSubscriptionTitle] = useState("");
  const [checkError, setCheckError] = useState<boolean>(false);
  // Annual pricing (2026-08-13) - the ONE toggle for both paid-tier cards
  // (Base Jump ignores it entirely, it's free either way). Lives here, not
  // per-card, because the toggle is a single global choice about how you
  // want to pay, not a property of any one plan.
  const [billingInterval, setBillingInterval] = useState<"monthly" | "annual">("monthly");

  // Proration preview (2026-08-15). Upgrades now charge IMMEDIATELY
  // (proration_behavior: 'always_invoice' on the backend) instead of adding
  // the difference to the next invoice, so the customer must be told what
  // they're about to be charged BEFORE they confirm - otherwise they see
  // "$89/month" and get billed $43.55 with no explanation, which is how a
  // charge becomes a dispute.
  //
  // These numbers come from Stripe's own upcoming-invoice preview, not from
  // any calculation on our side, so what's displayed is produced by the same
  // object that produces the charge.
  const [proration, setProration] = useState<any>(null);
  const [prorationLoading, setProrationLoading] = useState(false);

  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [successModal, setSuccessModal] = useState<boolean>(false);

  /*
   * Did the plan change actually SUCCEED? (2026-08-29)
   *
   * 🔴 This exists because both call sites below used to ask only whether a
   * response came BACK - `if (changeSubscription)` and
   * `if (changeSubscription?.data)`. A JSON-RPC error is returned as HTTP 200
   * with an `error` field, so an error object is truthy and the app declared
   * success on a FAILED PAYMENT: "Order Complete", "a receipt has been sent to
   * your email address", and the customer left believing they had upgraded.
   *
   * Caught live on 2026-08-29: Stripe rejected the change ("This customer has
   * no attached payment source or default payment method"), the account stayed
   * on Base Jump, no invoice was created - and the UI showed Order Complete.
   *
   * Presence is not success. A plan change counts only when the RPC returned a
   * result AND no error.
   */
  const planChangeSucceeded = (response: any) => {
    /*
     * 🔴 THE ONLY reliable signal is the ABSENCE of `error`.
     *
     * From JsonRpcWrapper (backend, Common/JsonRpc/jsonRpc.wrapper.ts):
     *   success -> { jsonrpc, result, id }   // `result` may be UNDEFINED
     *   failure -> { jsonrpc, error, id }    // no `result` field at all
     *
     * changeSubscription returns nothing, so a SUCCESSFUL call has
     * `result: undefined`. Two wrong versions of this check shipped in one
     * session, each with real consequences:
     *
     *   1. `if (response)` - an error object is truthy, so a FAILED payment
     *      showed "Order Complete" and a receipt that was never sent.
     *   2. `Boolean(response?.data?.result)` - a void method's undefined result
     *      looked like failure, so a SUCCESSFUL \$39 charge told the customer
     *      "your card has not been charged". It had been.
     *
     * Both directions are damaging, and the second is arguably worse: the
     * customer disputes a charge the app told them did not happen.
     */
    if (!response || !response.data) {
      return false;
    }

    return !response.data.error;
  };

  const planChangeError = (response: any) =>
    response?.data?.error?.message ||
    response?.data?.message ||
    "We couldn't complete the change. Your card has not been charged. Please check your payment details and try again.";


  const [loader, setLoader] = useState<boolean>(false);
  const [oldCard, setOldCard] = useState<boolean>(false);

  const [stripeValidation, setStripeValidation] = useState<any>({
    cardNumber: { message: "" },
    cardExpiry: { message: "" },
    cvc: { message: "" },
  });

  const [nameError, setNameError] = useState("");
  const [countryError, setCountryError] = useState("");
  const [cityError, setCityError] = useState("");

  const [planState, setPlanState] = useState("");
  const [detailsOpenTitle, setDetailsOpenTitle] = useState<string | null>(null);

  // Site-selection for downgrades that reduce the site allowance. Only shown
  // when the target plan allows fewer sites than the user currently owns.
  const [selectedSiteIdsToKeep, setSelectedSiteIdsToKeep] = useState<string[]>([]);

  const [errors, setErrors] = useState<IErrors>({
    incomplete_number: "",
    incomplete_expiry: "",
    incomplete_cvc: "",
    invalid_expiry_year_past: "",
    invalid_number: "",
  });
  useEffect(() => {
    if (checkError) {
      !authReducer.susbcriptionTitle &&
        setPlanState(translation("please_select_sub_plan"));
    }
  }, [checkError]);

  const getAccountRecord = async () => {
    const data = await subscription({
      jsonprc: "2.0",
      method: "getAccountRecord",
      params: {
        userId: authReducer.authData._id,
      },
      id: 0,
    });
    dispatch({
      type: ActionType.SUBSCRIPTIONDATA,
      payload: data.data?.result,
    });
    persistTierLocalStorage(data.data?.result?.subscriptionData);
  };
  /**
   * Fetches what this plan change will actually cost right now. Runs whenever
   * the selected plan or billing interval changes, since both alter the
   * proration. Deliberately fails SILENTLY (clears the panel rather than
   * showing an error): a preview is an aid, and a customer who cannot see one
   * should still be able to upgrade. The real charge is authoritative either
   * way - this only removes the surprise.
   */
  const fetchProration = async (title: string, interval: "monthly" | "annual") => {
    if (!title) {
      setProration(null);

      return;
    }

    setProrationLoading(true);
    try {
      const result = await subscription({
        jsonprc: "2.0",
        method: "previewSubscriptionChange",
        params: {
          userId: authReducer.authData._id,
          subscriptionTitle: title,
          billingInterval: interval,
        },
        id: 0,
      });
      setProration(result?.data?.result ?? null);
    } catch (error) {
      setProration(null);
    } finally {
      setProrationLoading(false);
    }
  };

  // Deps are deliberately just the two things that change the PRICE of this
  // change - the selected plan and the billing interval. fetchProration itself
  // is intentionally omitted: it is redefined every render, so including it
  // would refetch on every keystroke in the card form.
  useEffect(() => {
    fetchProration(subscriptionTitle, billingInterval);
  }, [subscriptionTitle, billingInterval]);

  // How many sites the TARGET plan allows. Uses the locally-selected plan title
  // (subscriptionTitle state), not the current account plan.
  const targetPlanWebsites: number = (() => {
    if (!subscriptionTitle) return 0;
    const plan = paymentFormPlans.find((p) => p.title === subscriptionTitle);
    return plan?.websites ?? 0;
  })();
  const ownedSites: any[] = authReducer?.authData?.sites ?? [];
  // Picker is required when (a) this is a scheduled downgrade and (b) the user
  // owns more sites than the target plan allows.
  const siteSelectorRequired =
    proration?.changeType === "downgrade" &&
    ownedSites.length > targetPlanWebsites &&
    targetPlanWebsites > 0;

  // Reset selections whenever the target plan or downgrade status changes.
  useEffect(() => {
    setSelectedSiteIdsToKeep([]);
  }, [subscriptionTitle, proration?.changeType]);

  /**
   *
   * Process Upgrade
   */
  const createSubscription = async () => {
    if (authReducer.subscriptionTitle) {
      if (paymentData) {
        setLoader(true);
        const changeSubscription = await subscription({
          jsonprc: "2.0",
          method: "changeSubscription",
          params: {
            userId: authReducer.authData._id,
            subscriptionTitle: authReducer?.subscriptionTitle,
            billingInterval: authReducer?.subscriptionBillingInterval,
            ...(siteSelectorRequired && selectedSiteIdsToKeep.length > 0
              ? { siteIdsToKeep: selectedSiteIdsToKeep }
              : {}),
          },
          id: 0,
        });

        if (planChangeSucceeded(changeSubscription)) {
          dispatch({
            type: ActionType.SUBSCRIPTIONTITLE,
            payload: { title: "" },
          });
          getAccountRecord();
          setLoader(false);
          setSuccessModal(true);
          const currentUserData: any = await currentUser();

          storage.set("siteId", currentUserData.data.site._id);

          if (currentUserData && currentUserData?.data) {
            dispatch({
              type: ActionType.LOGIN,
              payload: currentUserData.data,
            });
          }
        } else {
          // The real reason, not "something went wrong" - the customer needs to
          // know whether to fix their card or contact support, and needs to be
          // told plainly that they have NOT been charged.
          toastMessageError(planChangeError(changeSubscription));
          setLoader(false);
        }
      } else {
        if (!name) {
          setNameError(translation("card_name_required"));
        }

        if (!city) {
          setCityError(translation("card_city_required"));
        }

        if (!country) {
          setCountryError(translation("card_country_required"));
        }

        setLoader(true);
        const cardElement = elements?.getElement(CardNumberElement);
        const cardExpiryElement = elements?.getElement(CardExpiryElement);
        const cardCvcElement = elements?.getElement(CardCvcElement);

        try {
          if (cardElement && cardExpiryElement && cardCvcElement) {
            const paymentMethod = await stripe?.createPaymentMethod({
              type: "card",
              card: cardElement,
              billing_details: {
                name,
                address: {
                  country,
                  city,
                  postal_code: postalCode,
                },
              },
            });

            if (paymentMethod?.error && paymentMethod?.error?.code) {
              const tempErr: IErrors = errors;
              tempErr[paymentMethod?.error?.code as keyof IErrors] =
                paymentMethod?.error.message;

              setErrors((prevErrors) => ({
                [paymentMethod?.error?.code as keyof IErrors]:
                  paymentMethod?.error.message,
              }));
            }

            cardElement.on("change", function (event) {
              setErrors((prevErrors) => ({
                ...prevErrors,
                incomplete_number: "",
              }));
              setStripeValidation((prevValidation: any) => ({
                ...prevValidation,
                cardNumber: { message: event.error ? event.error.message : "" },
              }));
            });

            cardExpiryElement.on("change", function (event) {
              setErrors((prevErrors) => ({
                ...prevErrors,
                incomplete_expiry: "",
              }));
              setStripeValidation((prevValidation: any) => ({
                ...prevValidation,
                cardExpiry: { message: event.error ? event.error.message : "" },
              }));
            });

            cardCvcElement.on("change", function (event) {
              setErrors((prevErrors) => ({
                ...prevErrors,
                incomplete_cvc: "",
              }));
              setStripeValidation((prevValidation: any) => ({
                ...prevValidation,
                cvc: { message: event.error ? event.error.message : "" },
              }));
            });
            if (paymentMethod && paymentMethod.paymentMethod) {
              await subscription({
                jsonprc: "2.0",
                method: "setPaymentMethod",
                params: {
                  userId: authReducer.authData._id,
                  paymentMethod: {
                    id: paymentMethod?.paymentMethod?.id,
                    billingDetails:
                      paymentMethod?.paymentMethod?.billing_details,
                    customer: authReducer?.authData?.stripe_id,
                  },
                },
                id: 0,
              });

              const changeSubscription = await subscription({
                jsonprc: "2.0",
                method: "changeSubscription",
                params: {
                  userId: authReducer.authData._id,
                  subscriptionTitle: authReducer?.subscriptionTitle,
                  billingInterval: authReducer?.subscriptionBillingInterval,
                },
                id: 0,
              });

              if (planChangeSucceeded(changeSubscription)) {
                dispatch({
                  type: ActionType.SUBSCRIPTIONTITLE,
                  payload: { title: "" },
                });
                getAccountRecord();
                setSuccessModal(true);
                const currentUserData: any = await currentUser();

                storage.set("siteId", currentUserData.data.site._id);

                if (currentUserData && currentUserData?.data) {
                  dispatch({
                    type: ActionType.LOGIN,
                    payload: currentUserData.data,
                  });
                }
              } else {
                toastMessageError(planChangeError(changeSubscription));
              }
            }

            setLoader(false);
          }
        } catch (error: any) {
          // Was `console.log(error)` and nothing else - a thrown error left the
          // spinner running and told the customer nothing at all.
          toastMessageError(
            error?.message ||
              "We couldn't complete the change. Your card has not been charged."
          );
          setLoader(false);
        }
      }
    } else {
      setCheckError(true);

      setLoader(false);
    }
  };

  return (
    <>
      <div className="modal-body common-body text-primary upgrade-subscription">
        <div className="row g-3">
          {/* Left column: billing toggle + vertical accordion plan list */}
          <div className="col-lg-6">
            <div className="subscription-desc">
              <h4 className="subscription-heading">
                {translation("select_subscription")}
              </h4>
              {/* Annual pricing toggle - one global choice, not per-card.
                  Re-dispatches the selected plan with the new interval so
                  switching the toggle after picking a row doesn't leave a
                  stale interval in the store. Hardcoded strings because the
                  i18n resource fails to load on this account (every key logs
                  as "missingKey") - see the comment on the original toggle. */}
              <div className="billing-interval-toggle">
                <button
                  type="button"
                  className={billingInterval === "monthly" ? "active" : ""}
                  onClick={() => {
                    setBillingInterval("monthly");
                    if (selected !== undefined) {
                      const item = paymentFormPlans.find(
                        (plan) => plan.title === subscriptionTitle
                      );
                      if (item) {
                        dispatch({
                          type: ActionType.SUBSCRIPTIONTITLE,
                          payload: { ...item, billingInterval: "monthly" },
                        });
                      }
                    }
                  }}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={billingInterval === "annual" ? "active" : ""}
                  onClick={() => {
                    setBillingInterval("annual");
                    if (selected !== undefined) {
                      const item = paymentFormPlans.find(
                        (plan) => plan.title === subscriptionTitle
                      );
                      if (item) {
                        dispatch({
                          type: ActionType.SUBSCRIPTIONTITLE,
                          payload: { ...item, billingInterval: "annual" },
                        });
                      }
                    }
                  }}
                >
                  Annual{" "}
                  <span className="save-badge">Save 17%</span>
                </button>
              </div>

              {/* Horizontal accordion plan list — one row per tier.
                  Visibility rules:
                  - Base Jump shown on annual as disabled "Monthly only" row (no annual tier)
                  - On trial: all plans shown, none disabled
                  - Current plan shown as disabled with badge */}
              <div className="plan-accordion">
                {[...paymentFormPlans].reverse().map(
                  (item, _revKey) => {
                    const key = paymentFormPlans.length - 1 - _revKey;
                    const baseJumpMonthlyOnly = item.title === baseJumpPlan && billingInterval === "annual";
                    // Determine if this row is the user's current plan at current interval
                    const isCurrentPlan = currentPlan !== fourteenDaysPlan
                      ? item.title === currentPlan && billingInterval === currentBillingInterval
                      : false;
                    const isDisabled = isCurrentPlan || baseJumpMonthlyOnly;
                    return (
                      <div
                        key={item.title}
                        className={`plan-row${key === selected ? " active" : ""}${isCurrentPlan ? " current-plan-row" : ""}${baseJumpMonthlyOnly ? " monthly-only-row" : ""}`}
                      >
                        <div
                          className="plan-row-main"
                          onClick={() => {
                            if (isDisabled) return;
                            setSelected(key);
                            dispatch({
                              type: ActionType.SUBSCRIPTIONTITLE,
                              payload: { ...item, billingInterval },
                            });
                            setSubscriptionTitle(item.title);
                            setPlanState("");
                          }}
                        >
                          <div className="plan-row-identity">
                            {isCurrentPlan && (
                              <span className="current-plan-badge">YOUR CURRENT PLAN</span>
                            )}
                            {baseJumpMonthlyOnly && (
                              <span className="current-plan-badge monthly-only-badge">MONTHLY ONLY</span>
                            )}
                            <h5>{item.title}</h5>
                            <h6>{item.description}</h6>
                          </div>
                          <div className="plan-row-price">
                            <p className="price">
                              {billingInterval === "annual" && item.annualCost
                                ? item.annualCost
                                : item.cost}
                            </p>
                            <p className="period">
                              {billingInterval === "annual" && item.annualBillingPeriod
                                ? item.annualBillingPeriod
                                : item.billingPeriod}
                            </p>
                          </div>
                          <div className="plan-row-actions">
                            <CommonButton
                              className={
                                isDisabled
                                  ? "theme-btn success-outline-btn text-uppercase btn-sm"
                                  : key === selected
                                  ? "theme-btn success-btn text-uppercase btn-sm"
                                  : "theme-btn success-outline-btn text-uppercase btn-sm"
                              }
                              style={{ display: "block", textAlign: "center", width: "100%" }}
                              label={
                                isCurrentPlan
                                  ? "Current Plan  ✓"
                                  : baseJumpMonthlyOnly
                                  ? "Monthly Only"
                                  : key === selected
                                  ? "Selected  ✓"
                                  : `SELECT ${item.title.toUpperCase()}`
                              }
                              disabled={isDisabled}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                if (isDisabled) return;
                                setSelected(key);
                                dispatch({
                                  type: ActionType.SUBSCRIPTIONTITLE,
                                  payload: { ...item, billingInterval },
                                });
                                setSubscriptionTitle(item.title);
                                setPlanState("");
                              }}
                            />
                          </div>
                        </div>
                        {/* Always-visible specs strip */}
                        <div className="plan-row-specs">
                          <span>{item.pageviews} page views</span>
                          <span>{item.websites} {item.websites === 1 ? "website" : "websites"}</span>
                          <span>{item.dataStorage === 30 ? "30 days" : "1 year"} data</span>
                          <span>{item.urls} URLs</span>
                          <span>{item.mainPageviews} page slots</span>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
              <p className="auth-msg danger">{planState}</p>
            </div>
          </div>

          {/* Right column: payment details form */}
          <div className="col-lg-6">
            <div className="subscription-desc">
              <h4 className="subscription-heading">
                {translation("complete_payment_details")}
              </h4>
              <div className="card-form">
                {paymentData && addNewCard ? (
                  <div className="row">
                    <div className="col-md-12">
                      <div className="form-group">
                        <label>
                          {" "}
                          {`${paymentMethodData?.cardBrand} ${translation(
                            "ending_in"
                          )}`}
                        </label>
                        <p>{`****${paymentMethodData?.lastFourDigits}`}</p>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group">
                        <label> {"Card Address"}</label>
                        <p>{`${paymentMethodData?.name}, ${paymentMethodData?.address?.city} ${paymentMethodData?.address?.postalCode}, ${paymentMethodData?.address?.country}`}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <form>
                    <div className="row">
                      <div className="col-md-12">
                        <div className="form-group">
                          <label>{translation("name_on_card")}</label>
                          <input
                            id="name"
                            className="form-control"
                            placeholder={translation("name_on_card")}
                            type="text"
                            value={name}
                            onChange={(e) => {
                              setName(e.target.value);
                              setNameError("");
                            }}
                          />
                          {nameError && (
                            <p className="auth-msg danger">{nameError}</p>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>{translation("card_number")}</label>
                          <CardNumberElement
                            className="form-control"
                            id="cardNumber"
                          />
                          {(errors.incomplete_number ||
                            stripeValidation.cardNumber.message) && (
                            <p className="auth-msg danger">
                              {stripeValidation.cardNumber.message ? (
                                <span className="auth-msg danger">
                                  {stripeValidation.cardNumber.message}
                                </span>
                              ) : (
                                <span className="auth-msg danger">
                                  {errors.incomplete_number}
                                </span>
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group" id="myDiv">
                          <label>{translation("expiration_date")}</label>
                          <CardExpiryElement
                            className="form-control"
                            id="expiry"
                          />
                          {(errors.incomplete_expiry ||
                            stripeValidation.cardExpiry.message) && (
                            <p className="auth-msg danger">
                              {stripeValidation.cardExpiry.message
                                ? stripeValidation.cardExpiry.message
                                : errors.incomplete_expiry}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <label>{translation("cvc")}</label>
                          <CardCvcElement className="form-control" id="cvc" />
                          {(errors.incomplete_cvc ||
                            stripeValidation.cvc.message) && (
                            <p className="auth-msg danger">
                              {stripeValidation.cvc.message
                                ? stripeValidation.cvc.message
                                : errors.incomplete_cvc}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="form-inner-headiing">
                          <label>{translation("card_billing_address")}</label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>{translation("country")}</label>
                          <select
                            id="country"
                            onChange={(e) => {
                              setCountry(e.target.value);
                              setCountryError("");
                            }}
                            name="selectedReason"
                            className="form-select form-control country-select"
                          >
                            <option value="" disabled selected>
                              {translation("please_select")}
                            </option>
                            {countries?.map((item) => (
                              <option key={item.name} value={item.value}>
                                {item.name ? item.name : item.value}
                              </option>
                            ))}
                          </select>
                          {countryError && (
                            <p className="auth-msg danger">{countryError}</p>
                          )}
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>{translation("city_town")}</label>
                          <input
                            id="city"
                            className="form-control"
                            placeholder={translation("city_town")}
                            type="text"
                            value={city}
                            onChange={(e) => {
                              setCity(e.target.value);
                              setCityError("");
                            }}
                          />
                          {cityError && (
                            <p className="auth-msg danger">{cityError}</p>
                          )}
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label style={{ whiteSpace: "nowrap" }}>{translation("zip")}</label>
                          <input
                            className="form-control"
                            placeholder={translation("zip")}
                            type="text"
                            value={postalCode}
                            onChange={(e) => setPostalCode(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </form>
                )}

                <p className="encrypt-text">
                  <img src={LockIcon} alt="icon" />
                  {translation("secure_payment_with_rsa_encryption")}
                </p>
              </div>
          {paymentData && addNewCard ? (
                <CommonButton
                  className="transparent-btn"
                  label={translation("change_credit_debit_card")}
                  onClick={() => {
                    setPaymentData(null);
                    setAddNewCard(false);
                    setOldCard(true);
                  }}
                />
              ) : oldCard ? (
                <CommonButton
                  className="transparent-btn"
                  label="USE MY EXISTING CARD DETAILS"
                  onClick={() => {
                    setPaymentData(paymentMethodData);
                    setAddNewCard(true);
                  }}
                />
              ) : null}
            </div>
          </div>

          {/* Bottom row: proration + process upgrade — full width below both columns */}
          <div className="col-12">
            {/* Proration breakdown (2026-08-15). Upgrades charge immediately
                now, so this states plainly what is about to leave the
                customer's card and why, using Stripe's own line descriptions
                and amounts. Rendered only when there is genuinely something to
                charge today - on a brand-new subscription, or a change that
                nets to zero, an "amount due: $0.00" panel would be noise. */}
            {prorationLoading && (
              <div className="proration-preview loading">
                <p>Calculating what you'll be charged today…</p>
              </div>
            )}
            {/* A DOWNGRADE charges nothing today - it is scheduled for the end
                of the period already paid for. Before this existed the panel
                simply rendered nothing at all (its only condition was
                amountDue > 0, and a deferred downgrade is always $0), so a
                customer selecting a cheaper plan saw no explanation whatsoever
                and a button still labelled "Process Upgrade". Reported
                directly during staging testing: "when I select High Alt.
                monthly, nothing shows under the calculation. Also the same for
                Base Jump."
                changeType comes from the backend, which decides it with the
                same rule that performs the change - the UI never re-derives
                it. */}
            {!prorationLoading && proration && proration.changeType === "downgrade" && (
              <div className="proration-preview scheduled">
                <h5>Scheduled change</h5>
                <p className="proration-scheduled-lead">
                  You won't be charged today, and nothing changes right now.
                </p>
                <p className="proration-scheduled-detail">
                  You keep your current plan until{" "}
                  <strong>
                    {proration.effectiveDate
                      ? new Date(proration.effectiveDate * 1000).toLocaleDateString()
                      : "the end of your billing period"}
                  </strong>
                  . On that date you'll move to{" "}
                  <strong>{authReducer?.subscriptionTitle}</strong>
                  {proration.recurringAmountCents > 0 && (
                    <>
                      {" "}at ${(proration.recurringAmountCents / 100).toFixed(2)}/
                      {proration.recurringInterval}
                    </>
                  )}
                  .
                </p>
              </div>
            )}
            {/*
              An upgrade that costs NOTHING today still has to say so
              (2026-08-27). The panel below only rendered when
              amountDueCents > 0, so an upgrade Stripe prices at $0 right now -
              a trial line, or a credit that exactly covers it - showed
              "Calculating what you'll be charged today" and then rendered
              nothing at all. Reported directly: "it shows up then
              DISAPPEARS". A blank where a number was promised reads as a
              broken app at the exact moment the customer is deciding to pay.
            */}
            {!prorationLoading && proration && proration.changeType !== "downgrade" && proration.amountDueCents <= 0 && (
              <div className="proration-preview scheduled">
                <h5>Due today</h5>
                <p className="proration-scheduled-lead">
                  Nothing. You won't be charged today.
                </p>
                {proration.recurringAmountCents > 0 && (
                  <p className="proration-scheduled-detail">
                    Then{" "}
                    <strong>
                      ${(proration.recurringAmountCents / 100).toFixed(2)}
                    </strong>{" "}
                    per {proration.recurringInterval}
                    {proration.nextBillingDate
                      ? `, starting ${new Date(
                          proration.nextBillingDate * 1000
                        ).toLocaleDateString()}`
                      : ""}
                    .
                  </p>
                )}
              </div>
            )}
            {!prorationLoading && proration && proration.changeType !== "downgrade" && proration.amountDueCents > 0 && (
              <div className="proration-preview">
                <h5>Due today</h5>
                <ul className="proration-lines">
                  {proration.lines?.map((line: any, index: number) => (
                    <li key={index}>
                      <span className="proration-desc">{line.description}</span>
                      <span
                        className={
                          line.amountCents < 0
                            ? "proration-amount credit"
                            : "proration-amount"
                        }
                      >
                        {line.amountCents < 0 ? "−" : ""}$
                        {Math.abs(line.amountCents / 100).toFixed(2)}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="proration-total">
                  <span>Charged today</span>
                  <span>${(proration.amountDueCents / 100).toFixed(2)}</span>
                </div>
                {proration.recurringAmountCents > 0 && (
                  <p className="proration-then">
                    Then ${(proration.recurringAmountCents / 100).toFixed(2)}/
                    {proration.recurringInterval}
                    {proration.nextBillingDate
                      ? ` starting ${new Date(
                          proration.nextBillingDate * 1000
                        ).toLocaleDateString()}`
                      : ""}
                    .
                  </p>
                )}
              </div>
            )}
            {siteSelectorRequired && (
              <div className="site-selection-picker">
                <h5>Choose which sites to keep</h5>
                <p>
                  Your new plan includes{" "}
                  <strong>
                    {targetPlanWebsites} site
                    {targetPlanWebsites !== 1 ? "s" : ""}
                  </strong>
                  . You currently have {ownedSites.length}. Select the{" "}
                  {targetPlanWebsites === 1 ? "site" : `${targetPlanWebsites} sites`}{" "}
                  to keep. The other {ownedSites.length - targetPlanWebsites} will
                  be removed from your account
                  {proration?.effectiveDate
                    ? ` on ${new Date(proration.effectiveDate * 1000).toLocaleDateString()}`
                    : " when the plan change takes effect"}.
                </p>
                <ul className="site-selection-list">
                  {ownedSites.map((site: any) => {
                    const id = site._id?.toString() ?? site._id;
                    const checked = selectedSiteIdsToKeep.includes(id);
                    const disabled =
                      !checked &&
                      selectedSiteIdsToKeep.length >= targetPlanWebsites;
                    return (
                      <li key={id} className={`site-selection-item${disabled ? " disabled" : ""}${checked ? " selected" : ""}`}>
                        <label>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            onChange={() => {
                              if (checked) {
                                setSelectedSiteIdsToKeep((prev) =>
                                  prev.filter((s) => s !== id)
                                );
                              } else if (!disabled) {
                                setSelectedSiteIdsToKeep((prev) => [
                                  ...prev,
                                  id,
                                ]);
                              }
                            }}
                          />
                          <span className="site-selection-url">
                            {site.siteUrl ?? site.name ?? id}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
                {selectedSiteIdsToKeep.length < targetPlanWebsites && (
                  <p className="site-selection-hint">
                    Select {targetPlanWebsites - selectedSiteIdsToKeep.length}{" "}
                    more site
                    {targetPlanWebsites - selectedSiteIdsToKeep.length !== 1
                      ? "s"
                      : ""}
                  </p>
                )}
              </div>
            )}
            <div className="process-upgrade">
              {/* Was a static translation() string hardcoded to "monthly"
                  regardless of the toggle above - a real, confirmed bug:
                  selecting Annual never changed this text at all. Driven
                  directly off billingInterval state now instead of a
                  translation key, so it can never drift out of sync with
                  whatever the user actually has selected. */}
              {/* The authorization wording only makes sense when money is
                  actually about to move. For a scheduled downgrade nothing is
                  charged today, and telling someone they're authorizing a
                  charge would be simply untrue. */}
              {proration?.changeType === "downgrade" ? (
                <p>
                  Your plan change is scheduled and no payment is taken now.
                  Your existing plan continues until the end of the period
                  you've already paid for.
                </p>
              ) : (
                <p>
                  By submitting your payment information, you authorize us to
                  charge your card a recurring{" "}
                  {billingInterval === "annual" ? "annual" : "monthly"} fee in
                  accordance with your terms of service agreement with
                  Skydive.
                </p>
              )}
              <CommonButton
                label={
                  proration?.changeType === "downgrade"
                    ? "SCHEDULE PLAN CHANGE"
                    : translation("process_upgrade")
                }
                className="theme-btn btn-md success-btn text-uppercase"
                onClick={createSubscription}
                loading={loader}
                disabled={
                  !stripe ||
                  loader ||
                  (siteSelectorRequired &&
                    selectedSiteIdsToKeep.length < targetPlanWebsites)
                }
              />
            </div>
          </div>
        </div>
      </div>


      {/*
        A SCHEDULED DOWNGRADE IS NOT AN ORDER (fixed 2026-08-30).

        This always rendered "Order Complete" with "we have processed your order
        and a receipt has been sent" - for a downgrade, which takes NO payment.
        There is no order and no receipt, so a customer who goes looking for one
        will not find it, and the screen immediately before this correctly said
        "You won't be charged today". The flow contradicted itself across two
        consecutive screens.

        The `downgradeModel` variant already existed with the right copy; this
        call site simply never used it. `changeType` comes from the backend -
        the same value the scheduled-change block above keys off - so the
        confirmation now agrees with the quote the customer just read.
      */}
      {successModal && (
        <CongratsModel
          onClose={() => {setSuccessModal(false);}}
          name={proration?.changeType === "downgrade" ? "downgradeModel" : "OrderComplete"}
          title={proration?.changeType === "downgrade" ? "Plan Change Scheduled" : "Order Complete"}
          hasImage={true}
          header={true}
        />
      )}
    </>
  );
};

export default PaymentModel;
